import React, { useEffect, useRef, useState } from "react";
import api, { extractErrorMessage } from "@/lib/api";
import { useCompetition } from "@/context/CompetitionContext";
import CompetitionSelector from "@/components/CompetitionSelector";
import { Trash2, Edit, Music, Camera, Video, Paintbrush, Plus } from "lucide-react";
import AthleteBadges from "@/components/AthleteBadges";
import { DobrasField } from "@/components/DobrasField";
import { CategorySuggestions } from "@/components/CategorySuggestions";

const emptyForm = {
  nome: "",
  numero: "",
  equipe: "",
  cpf: "",
  treinador: "",
  category_id: "",
  musica: false,
  foto: false,
  video: false,
  pintura: false,
  dobras: [],
  sexo: "",
  altura_cm: "",
  peso_kg: "",
  data_nascimento: "",
};

export default function Athletes() {
  const { currentId, current } = useCompetition();
  const [cats, setCats] = useState([]);
  const [athletes, setAthletes] = useState([]);
  const [filter, setFilter] = useState("");
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState("");
  const [rules, setRules] = useState([]);
  const formRef = useRef(null);

  useEffect(() => {
    api.get("/division-rules").then((r) => setRules(r.data)).catch(() => setRules([]));
  }, []);

  const load = async (competitionChanged = false) => {
    if (!currentId) return;
    const [c, a] = await Promise.all([
      api.get(`/competitions/${currentId}/categories`),
      api.get(`/competitions/${currentId}/athletes`),
    ]);
    setCats(c.data);
    setAthletes(a.data);
    if (competitionChanged) {
      setEditingId(null);
      setError("");
      setForm({ ...emptyForm, category_id: c.data[0]?.id || "" });
    }
  };

  useEffect(() => {
    load(true);
    // eslint-disable-next-line
  }, [currentId]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const dobras = form.dobras.map((d) => d.v).filter(Boolean);
      if (new Set(dobras).size !== dobras.length) throw new Error("Não repita a mesma categoria em duas dobras.");
      if (dobras.includes(form.category_id)) throw new Error("A dobra deve ser em uma categoria diferente da principal.");
      const payload = {
        nome: form.nome,
        numero: form.numero ? parseInt(form.numero) : null,
        equipe: form.equipe,
        cpf: form.cpf,
        treinador: form.treinador,
        category_id: form.category_id,
        musica: !!form.musica,
        foto: !!form.foto,
        video: !!form.video,
        pintura: !!form.pintura,
        sexo: form.sexo || null,
        altura_cm: form.altura_cm ? parseFloat(String(form.altura_cm).replace(",", ".")) : null,
        peso_kg: form.peso_kg ? parseFloat(String(form.peso_kg).replace(",", ".")) : null,
        data_nascimento: form.data_nascimento || null,
      };
      if (editingId) {
        await api.put(`/athletes/${editingId}`, payload);
        const pending = [...form.dobras.filter((d) => d.v)];
        for (const row of [...pending]) {
          try {
            await api.post(`/athletes/${editingId}/add-dobra`, { category_id: row.v });
            pending.splice(pending.indexOf(row), 1);
          } catch (err) {
            setForm({ ...form, dobras: pending });
            await load();
            throw err;
          }
        }
      } else {
        await api.post(`/competitions/${currentId}/athletes`, { ...payload, dobra_category_ids: dobras });
      }
      setForm({ ...emptyForm, category_id: form.category_id });
      setEditingId(null);
      await load();
    } catch (e) {
      setError(e?.response ? extractErrorMessage(e) : e.message);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Excluir atleta e suas notas?")) return;
    await api.delete(`/athletes/${id}`);
    await load();
  };

  const startEdit = (a, withNewDobra = false) => {
    setError("");
    setEditingId(a.id);
    setForm({
      nome: a.nome,
      numero: a.numero,
      equipe: a.equipe || "",
      cpf: a.cpf || "",
      treinador: a.treinador || "",
      category_id: a.category_id,
      musica: !!a.musica,
      foto: !!a.foto,
      video: !!a.video,
      pintura: !!a.pintura,
      dobras: withNewDobra ? [{ k: `d-${Date.now()}`, v: "" }] : [],
      sexo: a.sexo || "",
      altura_cm: a.altura_cm ?? "",
      peso_kg: a.peso_kg ?? "",
      data_nascimento: a.data_nascimento || "",
    });
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const editingAthlete = editingId ? athletes.find((x) => x.id === editingId) : null;
  const existingDobras = editingAthlete?.profile_id
    ? athletes.filter((x) => x.id !== editingId && x.profile_id === editingAthlete.profile_id)
    : [];

  const removeExisting = async (d) => {
    const label = d.is_dobra ? "esta dobra" : "a inscrição principal";
    if (!window.confirm(`Remover ${label} (${catMap[d.category_id] || "categoria"})? As notas dela serão apagadas.`)) return;
    setError("");
    try {
      await api.delete(`/athletes/${d.id}`);
      await load();
    } catch (e) {
      setError(extractErrorMessage(e));
    }
  };

  const catMap = Object.fromEntries(cats.map((c) => [c.id, c.nome]));
  const filtered = athletes.filter(
    (a) =>
      !filter ||
      a.nome.toLowerCase().includes(filter.toLowerCase()) ||
      (a.equipe || "").toLowerCase().includes(filter.toLowerCase()) ||
      (catMap[a.category_id] || "").toLowerCase().includes(filter.toLowerCase())
  );

  // Group by profile_id -> count of inscriptions
  const profileCount = athletes.reduce((acc, a) => {
    if (!a.profile_id) return acc;
    acc[a.profile_id] = (acc[a.profile_id] || 0) + 1;
    return acc;
  }, {});

  const BADGES = [
    { key: "musica", label: "Música", Icon: Music },
    { key: "foto", label: "Foto", Icon: Camera },
    { key: "video", label: "Vídeo", Icon: Video },
    { key: "pintura", label: "Pintura", Icon: Paintbrush },
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <div>
          <div className="overline mb-2">Cadastro</div>
          <h1 className="font-display text-5xl font-black tracking-tight">ATLETAS</h1>
        </div>
        <CompetitionSelector />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1">
          <div className="card p-5 lg:sticky lg:top-6" ref={formRef}>
            <div className="overline mb-3">
              {editingId ? "Editar Atleta" : "Novo Atleta"}
            </div>
            <form onSubmit={submit} className="space-y-3">
              <select
                data-testid="atleta-cat"
                className="input-field"
                required
                value={form.category_id}
                onChange={(e) => setForm({ ...form, category_id: e.target.value })}
              >
                <option value="">Selecione a categoria</option>
                {cats.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.nome}
                  </option>
                ))}
              </select>
              <DobrasField
                cats={cats}
                catMap={catMap}
                principalId={form.category_id}
                rows={form.dobras}
                onChange={(rows) => setForm({ ...form, dobras: rows })}
                existing={existingDobras}
                onRemoveExisting={removeExisting}
              />
              <input
                data-testid="atleta-nome"
                className="input-field"
                placeholder="Nome do atleta"
                required
                value={form.nome}
                onChange={(e) => setForm({ ...form, nome: e.target.value })}
              />
              <input
                data-testid="atleta-numero"
                type="number"
                min={1}
                className="input-field"
                placeholder="Número (opcional, automático se vazio)"
                value={form.numero}
                onChange={(e) => setForm({ ...form, numero: e.target.value })}
              />
              <input
                data-testid="atleta-equipe"
                className="input-field"
                placeholder="Equipe / Academia"
                value={form.equipe}
                onChange={(e) => setForm({ ...form, equipe: e.target.value })}
              />
              <input
                data-testid="atleta-treinador"
                className="input-field"
                placeholder="Treinador (vinculado à equipe)"
                value={form.treinador}
                onChange={(e) => setForm({ ...form, treinador: e.target.value })}
              />
              <input
                data-testid="atleta-cpf"
                className="input-field"
                placeholder="CPF"
                value={form.cpf}
                onChange={(e) => setForm({ ...form, cpf: e.target.value })}
              />
              <div>
                <div className="overline mb-2">Perfil físico (para sugestão de categoria)</div>
                <div className="grid grid-cols-2 gap-2">
                  <select
                    data-testid="atleta-sexo"
                    className="select-field"
                    value={form.sexo}
                    onChange={(e) => setForm({ ...form, sexo: e.target.value })}
                  >
                    <option value="">Sexo</option>
                    <option value="M">Masculino</option>
                    <option value="F">Feminino</option>
                  </select>
                  <input
                    data-testid="atleta-nascimento"
                    type="date"
                    className="input-field"
                    value={form.data_nascimento}
                    onChange={(e) => setForm({ ...form, data_nascimento: e.target.value })}
                    title="Data de nascimento"
                  />
                  <input
                    data-testid="atleta-altura"
                    type="number"
                    step="0.1"
                    min="100"
                    max="230"
                    className="input-field"
                    placeholder="Altura (cm)"
                    value={form.altura_cm}
                    onChange={(e) => setForm({ ...form, altura_cm: e.target.value })}
                  />
                  <input
                    data-testid="atleta-peso"
                    type="number"
                    step="0.1"
                    min="30"
                    max="200"
                    className="input-field"
                    placeholder="Peso (kg)"
                    value={form.peso_kg}
                    onChange={(e) => setForm({ ...form, peso_kg: e.target.value })}
                  />
                </div>
              </div>
              <CategorySuggestions
                profile={{
                  sexo: form.sexo,
                  alturaCm: form.altura_cm ? parseFloat(String(form.altura_cm).replace(",", ".")) : null,
                  pesoKg: form.peso_kg ? parseFloat(String(form.peso_kg).replace(",", ".")) : null,
                  nascimento: form.data_nascimento,
                }}
                cats={cats}
                eventDate={current?.data}
                rules={rules}
                principalId={form.category_id}
                usedIds={new Set([...existingDobras.map((d) => d.category_id), ...form.dobras.map((d) => d.v)])}
                onPrincipal={(id) => setForm({ ...form, category_id: id })}
                onDobra={(id) => setForm({ ...form, dobras: [...form.dobras, { k: `d-${Date.now()}`, v: id }] })}
              />
              <div>
                <div className="overline mb-2">Adicionais</div>
                <div className="grid grid-cols-2 gap-2">
                  {BADGES.map(({ key, label, Icon }) => (
                    <label
                      key={key}
                      data-testid={`atleta-badge-toggle-${key}`}
                      className={
                        "flex items-center gap-2 px-3 py-2 border cursor-pointer text-xs select-none " +
                        (form[key]
                          ? "border-[#C9A84C] bg-[#C9A84C]/10 text-white"
                          : "border-white/10 text-[var(--text-secondary)]")
                      }
                    >
                      <input
                        type="checkbox"
                        className="sr-only"
                        checked={form[key]}
                        onChange={(e) => setForm({ ...form, [key]: e.target.checked })}
                      />
                      <Icon size={13} strokeWidth={2} />
                      <span>{label}</span>
                    </label>
                  ))}
                </div>
              </div>
              {error && (
                <div className="text-sm text-[#C9A84C] border border-[#C9A84C]/30 bg-[#C9A84C]/10 px-3 py-2">
                  {error}
                </div>
              )}
              <div className="flex gap-2">
                <button data-testid="atleta-submit" className="btn-primary flex-1">
                  {editingId ? "Atualizar" : "Cadastrar"}
                </button>
                {editingId && (
                  <button
                    type="button"
                    data-testid="atleta-cancel"
                    className="btn-ghost"
                    onClick={() => {
                      setEditingId(null);
                      setForm({ ...emptyForm, category_id: form.category_id });
                    }}
                  >
                    Cancelar
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>

        <div className="lg:col-span-2">
          <div className="mb-3">
            <input
              data-testid="atleta-filter"
              className="input-field"
              placeholder="Filtrar por nome, equipe ou categoria..."
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />
          </div>
          <div className="card overflow-x-auto">
            <table className="table-dense">
              <thead>
                <tr>
                  <th className="w-14">Nº</th>
                  <th>Atleta</th>
                  <th>Categoria</th>
                  <th>Equipe</th>
                  <th className="text-right pr-4">Ações</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((a) => (
                  <tr key={a.id} data-testid={`atleta-row-${a.id}`}>
                    <td className="tabular-nums font-display font-bold">
                      {a.numero}
                    </td>
                    <td>
                      {a.nome}
                      {a.is_dobra && (
                        <span
                          className="ml-2 px-1.5 py-0.5 text-[10px] font-bold tracking-widest bg-[#C9A84C] text-[#0A0A0A] rounded-sm align-middle"
                          data-testid={`atleta-dobra-badge-${a.id}`}
                        >
                          DOBRA
                        </span>
                      )}
                      {a.profile_id && profileCount[a.profile_id] > 1 && !a.is_dobra && (
                        <span
                          className="ml-2 px-1.5 py-0.5 text-[10px] font-bold tracking-widest border border-[#C9A84C] text-[#C9A84C] rounded-sm align-middle"
                          title={`Perfil único com ${profileCount[a.profile_id]} inscrições`}
                          data-testid={`atleta-perfil-badge-${a.id}`}
                        >
                          PERFIL ×{profileCount[a.profile_id]}
                        </span>
                      )}
                      <AthleteBadges athlete={a} />
                    </td>
                    <td className="text-xs text-[var(--text-secondary)]">
                      {catMap[a.category_id]}
                    </td>
                    <td>{a.equipe || "—"}</td>
                    <td className="text-right pr-4 whitespace-nowrap">
                      <button
                        className="btn-ghost mr-1"
                        title="Adicionar Dobra (nova categoria para este atleta)"
                        data-testid={`atleta-add-dobra-${a.id}`}
                        onClick={() => startEdit(a, true)}
                      >
                        <Plus size={14} />
                      </button>
                      <button
                        className="btn-ghost mr-1"
                        data-testid={`atleta-edit-${a.id}`}
                        onClick={() => startEdit(a)}
                      >
                        <Edit size={14} />
                      </button>
                      <button className="btn-ghost" data-testid={`atleta-delete-${a.id}`} onClick={() => del(a.id)}>
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr>
                    <td
                      colSpan={5}
                      className="text-center py-8 text-[var(--text-secondary)]"
                    >
                      Nenhum atleta encontrado.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
